import { LabelAssociationsSyncRequestConfig, LabelsSyncRequestConfig, useStorytellClient } from "@storytell/client";
import { subscribeToGlobalEvent } from "@storytell/events";
import type { Label, LabelAssociation } from "@storytell/types";
import { createResource, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";
import { createResourceStorage } from "~/lib/createResourceStorage";
import { createEntitySyncManager } from "../sync/createEntitySyncManager";

export type LabelsService = ReturnType<typeof createLabelsService>;

export const createLabelsService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
}) => {
  const client = useStorytellClient();

  const labels = createEntitySyncManager<Label & { userId: string }>(undefined, (label) => [
    label.name,
    label.description,
  ]);

  const storage = createResourceStorage(
    () => labels.all(),
    (value) => labels.hydrateFromSync(value || []),
  );

  const [labelsData, { refetch }] = createResource(
    () => !isServer && deps.authReady() && deps.userId(),
    async (): Promise<(Label & { userId: string })[]> => {
      const userId = deps.userId();
      if (!userId) return [];
      const request = LabelsSyncRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      if (response.isErr()) {
        console.error("Failed to fetch labels sync", response.error);
        return [];
      }

      const data = Object.values(response.value.labels).map((label) => ({ ...label, userId }));
      if (isServer) {
        storage[1](data);
      }
      return data;
    },
    {
      initialValue: [] as (Label & { userId: string })[],
      deferStream: true,
      storage: () => storage,
    },
  );

  const [labelAssociations, { refetch: refetchLabelAssociations, mutate: mutateLabelAssociations }] = createResource(
    () => !isServer && deps.authReady() && deps.userId(),
    async (): Promise<LabelAssociation[]> => {
      const request = LabelAssociationsSyncRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      if (response.isErr()) {
        console.error("Failed to fetch labels sync", response.error);
        return [];
      }

      return response.value as LabelAssociation[];
    },
    {
      initialValue: [],
      deferStream: true,
    },
  );

  onMount(() => {
    const unsubscribe = subscribeToGlobalEvent("user-context-changed", () => {
      refetch();
    });
    onCleanup(() => {
      unsubscribe();
    });
  });

  // This is used to trigger a suspense boundary so SSR works properly
  // Don't use it for data (that's why it's returned as undefined)
  const suspenseTrigger = () => {
    labelsData();
    labelAssociations();
    return undefined;
  };
  const getAssociation = (labelId: string, resourceId: string) => {
    return labelAssociations.latest.find(
      (association) => association.labelId === labelId && association.resourceId === resourceId,
    );
  };

  const onLabelAssociated = (association: LabelAssociation) => {
    mutateLabelAssociations((associations) => [...associations, association]);
  };
  const onLabelDisassociated = (associationId: string) => {
    mutateLabelAssociations((associations) => associations.filter((association) => association.id !== associationId));
  };

  return {
    suspenseTrigger,
    refetch,
    refetchLabelAssociations,
    getAssociation,
    onLabelAssociated,
    onLabelDisassociated,
    ...labels,
  };
};
